import React, { useContext, useState, useMemo, useEffect, useRef } from "react";
import { FaSearch, FaTimes } from "react-icons/fa";
import { useNavigate, useLocation } from "react-router-dom";
import debounce from "lodash.debounce";
import { Language } from "../../App";
import RelatedSearches from "./RelatedSearches";

const CourseSearchBox = ({ result, onSearch, initialValue = "" }) => {
  const { lang } = useContext(Language);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState(initialValue || "");
  const inputRef = useRef(null);

  // Keep a stable ref to onSearch so debounce is never recreated
  const onSearchRef = useRef(onSearch);
  useEffect(() => {
    onSearchRef.current = onSearch;
  }, [onSearch]);

  // Stable debounced function (500ms)
  const debouncedSearch = useMemo(
    () =>
      debounce((value) => {
        onSearchRef.current?.(value);
      }, 500),
    []
  );

  // Sync internal input state when initialValue changes externally (e.g. Clear Filters)
  useEffect(() => {
    if (initialValue !== searchTerm) {
      setSearchTerm(initialValue || "");
    }
  }, [initialValue]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    debouncedSearch(value);
  };

  const handleInputClick = () => {
    if (location.pathname !== "/courses/filter") {
      navigate("/courses/filter");
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      debouncedSearch.cancel();
      onSearchRef.current?.(searchTerm);
      handleInputClick();
    }
  };

  const handleClearSearch = () => {
    debouncedSearch.cancel();
    setSearchTerm("");
    onSearchRef.current?.("");
    inputRef.current?.focus();
  };

  const isFilterRoute = location.pathname === "/courses/filter";
  const placeholderText = lang?.search_course || "Search courses, topics, specialties...";
  const headingText = lang?.what_do_you_want_to_learn || "What do you want to learn today?";

  return (
    <div className="cr-search-box position-relative">
      <h3 className="cr-search-heading">{headingText}</h3>
      <div className="cr-search-field">
        <label
          htmlFor="course-search"
          style={{
            position: "absolute",
            width: "1px",
            height: "1px",
            padding: 0,
            margin: "-1px",
            overflow: "hidden",
            clip: "rect(0,0,0,0)",
            border: 0,
          }}
        >
          {placeholderText}
        </label>
        <FaSearch className="cr-search-icon-left" aria-hidden="true" />
        <input
          id="course-search"
          type="text"
          value={searchTerm}
          onChange={handleSearchChange}
          onClick={handleInputClick}
          onKeyDown={handleKeyDown}
          ref={inputRef}
          className="cr-search-input"
          placeholder={placeholderText}
          autoFocus={isFilterRoute}
        />
        {searchTerm && (
          <FaTimes
            className="cr-search-icon-clear"
            onClick={handleClearSearch}
            aria-label="Clear search"
          />
        )}
      </div>

      {!result && searchTerm && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            width: "100%",
            left: 0,
            right: 0,
            zIndex: 10,
            marginTop: "6px",
          }}
        >
          <RelatedSearches searchTerm={searchTerm} />
        </div>
      )}
    </div>
  );
};

export default React.memo(CourseSearchBox);